import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import { PendingApproval } from '@models/task.model';
import { BudgetRequest } from '@models/budget.model';
import { Incident } from '@models/incident.model';
import { ApprovalItem } from '@components/organisms/ApprovalsNotification/components/ApprovalItem';
import { RejectionModal } from '@components/molecules/RejectionModal';
import { useTaskApprovalActions } from '@hooks/api/task/useTaskApprovalActions';
import {
  NeedsYourActionGroup,
  NeedsYourActionGroupKey,
} from './components/NeedsYourActionGroup';
import { BidActionItem } from './components/BidActionItem';
import { IncidentActionItem } from './components/IncidentActionItem';

interface NeedsYourActionBlockProps {
  approvals: PendingApproval[];
  approvalsLoading: boolean;
  bids: BudgetRequest[];
  bidsLoading: boolean;
  incidents: Incident[];
  incidentsLoading: boolean;
  onRefetchApprovals: () => void;
}

/**
 * Sprint 16, story-075 + story-076 (i03ePmni + GRpkXE4U). Top-of-feed "Needs your action" block:
 * lists pending approvals, bids, and incidents from the SAME hooks that produce the sidebar's
 * counts, so the count shown always equals the number of items listed. Replaces the sidebar's
 * clipped `ApprovalsNotification` panel (now deleted) and gives the bid/incident feed cards a
 * shared navigation destination with this block's own rows.
 *
 * Presentational — hooks stay mounted at the page level (`dashboard.tsx`), matching this
 * codebase's existing convention (incidents/bids were already fetched directly there).
 */
export const NeedsYourActionBlock: React.FC<NeedsYourActionBlockProps> = ({
  approvals,
  approvalsLoading,
  bids,
  bidsLoading,
  incidents,
  incidentsLoading,
  onRefetchApprovals,
}) => {
  const history = useHistory();
  const [expandedGroups, setExpandedGroups] = useState<Set<NeedsYourActionGroupKey>>(new Set());

  const {
    loadingTaskId,
    rejectTarget,
    handleApprove,
    handleReject,
    confirmReject,
    closeRejectModal,
  } = useTaskApprovalActions(onRefetchApprovals);

  const toggleGroup = useCallback((key: NeedsYourActionGroupKey) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const handleViewApproval = useCallback(
    (approval: PendingApproval) => {
      history.push(`/schedule/${approval.projectId}/${approval.taskId}`);
    },
    [history],
  );

  const handleViewBid = useCallback(
    (budgetRequestId: string) => {
      history.push(`/budgets?budgetRequestId=${budgetRequestId}`);
    },
    [history],
  );

  const handleViewIncident = useCallback(
    (incidentId: string) => {
      history.push(`/incidents/${incidentId}`);
    },
    [history],
  );

  const totalCount = approvals.length + bids.length + incidents.length;
  const anyLoading = approvalsLoading || bidsLoading || incidentsLoading;

  // Nothing pending and nothing still on its first, uncached load — matches
  // `ApprovalsNotification`'s existing "don't render count when 0" convention.
  if (totalCount === 0 && !anyLoading) {
    return null;
  }

  return (
    <div
      data-testid="needs-your-action-block"
      className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
      <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
        <WarningAmberOutlinedIcon fontSize="small" className="text-amber-500" />
        Needs your action{totalCount > 0 && ` (${totalCount})`}
      </h2>

      {totalCount === 0 && anyLoading ? (
        <div className="space-y-2" aria-hidden="true">
          <div className="h-10 bg-gray-100 rounded animate-pulse" />
          <div className="h-10 bg-gray-100 rounded animate-pulse" />
          <div className="h-10 bg-gray-100 rounded animate-pulse" />
        </div>
      ) : (
        <>
          <NeedsYourActionGroup
            groupKey="approvals"
            icon={<CheckCircleOutlineIcon fontSize="small" className="text-blue-600" />}
            label="Approvals"
            count={approvals.length}
            expanded={expandedGroups.has('approvals')}
            onToggleExpand={() => toggleGroup('approvals')}
            items={approvals.map((approval) => (
              <ApprovalItem
                key={approval.taskId}
                approval={approval}
                isLoading={loadingTaskId === approval.taskId}
                onView={handleViewApproval}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            ))}
          />

          <NeedsYourActionGroup
            groupKey="bids"
            icon={<TextSnippetOutlinedIcon fontSize="small" className="text-indigo-500" />}
            label="Bids"
            count={bids.length}
            expanded={expandedGroups.has('bids')}
            onToggleExpand={() => toggleGroup('bids')}
            items={bids.map((bid) => (
              <BidActionItem
                key={bid.budgetRequestId}
                budgetRequest={bid}
                onView={handleViewBid}
              />
            ))}
          />

          <NeedsYourActionGroup
            groupKey="incidents"
            icon={<WarningAmberOutlinedIcon fontSize="small" className="text-red-600" />}
            label="Incidents"
            count={incidents.length}
            expanded={expandedGroups.has('incidents')}
            onToggleExpand={() => toggleGroup('incidents')}
            items={incidents.map((incident) => (
              <IncidentActionItem
                key={incident.incidentId}
                incident={incident}
                onView={handleViewIncident}
              />
            ))}
          />
        </>
      )}

      <RejectionModal
        isOpen={!!rejectTarget}
        onClose={closeRejectModal}
        onConfirm={confirmReject}
        loading={!!loadingTaskId}
      />
    </div>
  );
};

export default NeedsYourActionBlock;
